import "./styles.scss";

import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Collapse } from "react-collapse";
import classnames from "classnames";
import moment from "moment";

import ModalNew from "components/ModalNew";

import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";

import { ReactComponent as TrashIcon } from "assets/icons/new/trash.svg";
import { ReactComponent as PencilIcon } from "assets/icons/new/pencil.svg";
import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";

const mainClass = "task-view-main-tracker-work";

const ModalWork = ({
  time_entries,
  actions_available,
  isOpen,
  isCompletelyDisabled,
  onDelete,
  onEdit,
  onClose,
}) => {
  const { t } = useTranslation();

  const [user, setUser] = useState(null);

  const groupedTimeEntries = useMemo(() => {
    if (!time_entries || time_entries.length === 0) return {};

    return time_entries.reduce((acc, entry) => {
      const userId = entry.webmaster._id;

      if (!acc[userId]) {
        acc[userId] = {
          user: entry.webmaster,
          entries: [],
          totalTime: 0,
        };
      }

      acc[userId].entries.push({
        _id: entry._id,
        created_at: entry.created_at,
        started_at: entry.started_at,
        time: toHoursAndMinutes(entry.time?.$numberDecimal || entry.time || 0)
          ?.formatted,
      });

      const timeValue = entry.time?.$numberDecimal || entry.time || 0;
      acc[userId].totalTime += parseFloat(timeValue);

      const isLastEntry =
        time_entries
          .filter((e) => e.webmaster._id === userId)
          .indexOf(entry) ===
        time_entries.filter((e) => e.webmaster._id === userId).length - 1;

      if (isLastEntry)
        acc[userId].totalTime = toHoursAndMinutes(
          acc[userId].totalTime,
        )?.formatted;

      return acc;
    }, {});
  }, [time_entries]);

  return (
    <ModalNew
      title={t("Work time")}
      variant="medium"
      isOpen={isOpen}
      isSubmitHidden
      onClose={() => {
        if (typeof onClose === "function") onClose();

        setUser(null);
      }}
    >
      <div className={mainClass}>
        {Object.keys(groupedTimeEntries)?.length > 0 ? (
          Object.entries(groupedTimeEntries).map(([id, data]) => {
            const isOpen = user === id;

            return (
              <div
                key={id}
                className={classnames(`${mainClass}__user`, {
                  [`${mainClass}__user--open`]: isOpen,
                })}
              >
                <div className={`${mainClass}__user-header`}>
                  <div className={`${mainClass}__user-details`}>
                    <div>
                      <img src={data.user.photo?.location} alt="" />
                    </div>
                    <p>
                      {data.user.first_name} {data.user.last_name}
                    </p>
                  </div>
                  <div className={`${mainClass}__user-time`}>
                    {data.totalTime}
                  </div>
                  {actions_available && !isCompletelyDisabled && (
                    <div className={`${mainClass}__user-actions`}>
                      <button
                        type="button"
                        onClick={
                          typeof onDelete === "function"
                            ? () =>
                                onDelete({
                                  type: "total_time",
                                  id,
                                  name: `${data.user.first_name} ${data.user.last_name}`,
                                  time: data.totalTime,
                                })
                            : undefined
                        }
                      >
                        <TrashIcon />
                      </button>
                    </div>
                  )}
                  <div className={`${mainClass}__user-arrow`}>
                    <ArrowIcon />
                  </div>
                  <button
                    className={`${mainClass}__user-expand`}
                    type="button"
                    onClick={() =>
                      setUser((prevId) => (prevId === id ? null : id))
                    }
                  />
                </div>
                <Collapse isOpened={isOpen}>
                  <div className={`${mainClass}__user-entries`}>
                    {data.entries.map((entry) => (
                      <div
                        key={entry._id}
                        className={classnames({
                          "no-actions": !actions_available,
                        })}
                      >
                        <div className={`${mainClass}__user-entries-date`}>
                          {moment(entry.created_at).format("LL")}
                        </div>
                        <div className={`${mainClass}__user-entries-time`}>
                          {entry.time}
                        </div>
                        {actions_available && !isCompletelyDisabled && (
                          <div className={`${mainClass}__user-entries-actions`}>
                            <button
                              type="button"
                              onClick={
                                typeof onEdit === "function"
                                  ? () =>
                                      onEdit({
                                        id: {
                                          webmaster: id,
                                          time: entry._id,
                                        },
                                        date: moment(entry.created_at).format(
                                          "LL",
                                        ),
                                        time: entry.time,
                                      })
                                  : undefined
                              }
                            >
                              <PencilIcon />
                            </button>
                            <button
                              type="button"
                              onClick={
                                typeof onDelete === "function"
                                  ? () =>
                                      onDelete({
                                        type: "entry",
                                        id: entry._id,
                                        name: `${data.user.first_name} ${data.user.last_name}`,
                                        time: entry.time,
                                      })
                                  : undefined
                              }
                            >
                              <TrashIcon />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Collapse>
              </div>
            );
          })
        ) : (
          <p>{t("Working time has not been recorded yet.")}</p>
        )}
      </div>
    </ModalNew>
  );
};

export default ModalWork;
